import React from 'react';
import { Route } from 'shared/types/ObjectTypes';
import { useTranslation } from 'react-i18next';
import { getRouteStatus, getStatusDotColor } from './getRouteStatus';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import { routeDetailsClasses } from 'styles/tailwindClassnames';
import { routeDisplayName } from './routeDisplayName';
import { ROUTE_DETAIL_TABS } from './routeSubpages';

type RouteDetailsProps = {
  route: Route;
  // The provider integration's on/off state: a paused Route's last-known health
  // means nothing, so the dot goes gray while this is false.
  providerEnabled?: boolean;
  // Unsaved edit of the Route's own name (decoupled from the provider's) —
  // shown here so the header tracks what the General tab is typing.
  routeName?: string;
  subPage: string | undefined;
  onSelectSubPage: Function;
};

const RouteDetails = ({
  route,
  providerEnabled = true,
  routeName,
  subPage,
  onSelectSubPage,
}: RouteDetailsProps) => {
  const { t } = useTranslation();
  // A disabled Route reports its last analysis, not its present — show it as
  // "Disabled" (gray), the same way the flow map suppresses health while off.
  const dotStatus = providerEnabled ? route.status : 'disabled';

  return (
    /* Two siblings, not one wrapper: the title block scrolls away normally
           while the tab strip below is STICKY. A sticky element can only travel
           inside its own parent, so both have to be direct children of the
           page-height column in ManageRouteDetails — wrapping them in a
           header-height box would pin the tabs to the header again. */
    <>
      <div className={routeDetailsClasses.container}>
        <div className={routeDetailsClasses.header}>
          <div className="mx-auto max-w-list-table">
            {/* page header — the Route's on/off switch lives on the flow map's
                    outgoing arrow, not here. */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
              <div className="flex flex-col md:w-3/4">
                {/* The dot lives INSIDE the h1, as inline content after
                            the name — not as a sibling in a flex row. As a
                            sibling it sat after the heading's whole box, so a
                            name that wrapped to two lines left the dot stranded
                            far to the right and vertically centred between the
                            lines. Inline, it simply follows the last word. */}
                <h1 className={routeDetailsClasses.title}>
                  {routeName ?? routeDisplayName(route)}
                  {/* The shared health dot (getStatusDotColor — same mapping as the
                                flow map panel), so the status stays visible on Sources/Logs
                                where the map isn't. */}
                  {dotStatus && (
                    /* Focusable (the default): the dot is the header's only
                                   status signal, so its text must be reachable by keyboard
                                   and assistive tech, not hover-only. */
                    <HoverTooltip
                      content={getRouteStatus(dotStatus, true, t).text}
                      position="top"
                      portal
                      className="ml-3 align-middle"
                    >
                      <span
                        data-testid="RouteDetails-status-dot"
                        className={`block h-2.5 w-2.5 rounded-full ${getStatusDotColor(dotStatus)}`}
                      />
                    </HoverTooltip>
                  )}
                </h1>
                <div className="mb-3 mt-2 flex flex-row items-center gap-2">
                  {route?.provider?.type?.name && (
                    <span className={routeDetailsClasses.headerPill}>
                      {route.provider.type.name}
                    </span>
                  )}
                  {route?.owner?.name && (
                    <span className={routeDetailsClasses.headerPill}>
                      {route.owner.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* tabs for sub-pages — bidirectional redesign: technology-named tabs.
            Sticky directly under the fixed top bar — 70px on desktop, the 80px
            mobile bar below md (routeDetailsClasses.stickyTabs owns the numbers)
            — so the tabs stay reachable however far the Configurations tab
            scrolls. This replaced the floating vertical rail, which solved the
            same problem by appearing once the header had left the viewport. */}
      <div
        data-testid="RouteDetails-tabs"
        className={routeDetailsClasses.stickyTabs}
      >
        {/* Two divs on purpose: the centering (max-w + mx-auto) has to be
                on its own element — putting the tab row's -ml-3 on the same one
                overrides margin-left:auto and the strip drifts off the content
                column. px-4 matches the inset of the panels below, so the first
                tab's label and its green underline start exactly where the
                content container does. */}
        <div className="mx-auto max-w-list-table px-4">
          <div className="-ml-3 flex w-full flex-row font-bold" role="tablist">
            {ROUTE_DETAIL_TABS.map(({ slug, labelKey }) => (
              <label
                key={slug}
                className={(subPage === slug
                  ? routeDetailsClasses.tabActive
                  : routeDetailsClasses.tabInactive
                ).replace('mr-3', 'mx-3')}
                data-testid={`RouteDetails-${slug}-tab`}
                role="tab"
                tabIndex={0}
                aria-selected={subPage === slug}
                onClick={() => onSelectSubPage(slug)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSelectSubPage(slug);
                  }
                }}
              >
                {t(labelKey)}
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default RouteDetails;
